import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../client';
import { snapshotIds, useResilientMutation } from '../useResilientMutation';
import { verifyVersionPublished, type PublishVars } from '../verifiers';
import {
  versionApi,
  type OntologyVersion,
  type PublishOptions,
  type PublishResponse,
} from '../../api/version.api';

/**
 * Hook: list all versions of an ontology (draft + published).
 */
export function useVersions(ontologyId: string | undefined) {
  return useQuery<OntologyVersion[]>({
    queryKey: queryKeys.versions(ontologyId ?? ''),
    queryFn: () => versionApi.list(ontologyId as string),
    enabled: !!ontologyId,
  });
}

/**
 * Hook: assign the version number to the current draft, freeze it, and fork a
 * new unnumbered draft.
 *
 * On success we invalidate the versions list AND the ontology query itself: the
 * lists derive their version label from the version graph, so both go stale.
 */
export function usePublishVersion(ontologyId: string | undefined) {
  const queryClient = useQueryClient();

  const mutation = useResilientMutation<PublishResponse, PublishVars>({
    mutationFn: (vars) => versionApi.publish(vars.ontologyId, vars.options),
    verify: verifyVersionPublished,
    onSuccess: () => {
      if (!ontologyId) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.versions(ontologyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.ontology(ontologyId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.ontologies });
    },
  });

  const submit = (options: PublishOptions) => {
    if (!ontologyId) return Promise.resolve(null);
    const cached = queryClient.getQueryData<OntologyVersion[]>(queryKeys.versions(ontologyId));
    return mutation.submit({
      ontologyId,
      options,
      knownIds: snapshotIds(cached),
      snapshotAvailable: Array.isArray(cached),
    });
  };

  return { ...mutation, submit };
}

// Made with Bob
